import type { KeyboardEvent } from 'react';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Box } from '~/eds/src/components/Box/Box';
import { Card } from '~/eds/src/components/Card/Card';
import { GradientText } from '~/eds/src/components/GradientText/GradientText';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { LoadingShimmer } from '~/eds/src/components/LoadingShimmer/LoadingShimmer';
import { StatusText } from '~/eds/src/components/StatusText/StatusText';
import { Text } from '~/eds/src/components/Text/Text';

import { Message as MessageType } from '../../types/chat';

export interface WorkflowRoutingWelcomeProps {
  onChoose: (message: MessageType, choice: string) => void;
  suggestions?: string[] | null;
  instructions?: string;
  initialSuggestionsFailedToLoad?: boolean;
}

export const WorkflowRoutingWelcome = ({
  onChoose,
  suggestions,
  instructions,
  initialSuggestionsFailedToLoad,
}: WorkflowRoutingWelcomeProps) => {
  const intl = useIntl();
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    if (
      (suggestions !== undefined && suggestions !== null) ||
      initialSuggestionsFailedToLoad
    ) {
      setIsInitialLoad(false);
    }
  }, [suggestions, initialSuggestionsFailedToLoad]);

  const hasSuggestions = suggestions && suggestions.length > 0;

  const handleSuggestionClick = (suggestion: string) => {
    const welcomeMessage: MessageType = {
      id: 'welcome',
      text: '',
      userType: 'other',
      timestamp: new Date(),
      choices: [],
      citations: [],
      sources: [],
    };
    onChoose(welcomeMessage, suggestion);
  };

  const headerContent = (
    <Layout direction="column" spacing={8} mb={4}>
      <Layout direction="column" spacing={4}>
        <StatusText
          icon="ai-filled"
          iconSpacing="s"
          text={intl.formatMessage({
            id: 'WorkflowRoutingWelcome.title',
            defaultMessage: 'Workflow Routing',
            description: 'Title for workflow routing welcome',
          })}
          variant="body-medium"
        />
        <GradientText
          variant="title"
          fontWeight="medium"
          fromColor="brand"
          toColor="brand"
          toColorOpacity={40}
          mb={3}
        >
          {intl.formatMessage({
            id: 'WorkflowRoutingWelcome.mainMessage',
            defaultMessage: 'Ask AI to find the correct workflow',
            description: 'Main message for workflow routing welcome',
          })}
        </GradientText>
      </Layout>
      {instructions && (
        <Box
          bg="status.active.light"
          borderRadius="l"
          color="text.primary"
          p={4}
        >
          <Text variant="body">{instructions}</Text>
        </Box>
      )}
    </Layout>
  );
  return (
    <Layout
      w="100%"
      h="100%"
      direction="column"
      align="center"
      styles={{ position: 'relative' }}
    >
      <Layout
        direction="column"
        w="90%"
        maxW="680px"
        px={6}
        spacing={4}
        styles={{ position: 'absolute', top: '64px' }}
      >
        {headerContent}
        {isInitialLoad &&
          !hasSuggestions &&
          !initialSuggestionsFailedToLoad && (
            <Layout direction="column" spacing={3} w="100%">
              <LoadingShimmer height="64px" width="100%" mode="rectangular" />
              <LoadingShimmer height="64px" width="100%" mode="rectangular" />
              <LoadingShimmer height="64px" width="100%" mode="rectangular" />
            </Layout>
          )}

        {initialSuggestionsFailedToLoad && (
          <Layout direction="column" spacing={4} w="100%">
            <StatusText
              icon="info"
              iconColor="status.warning"
              text={intl.formatMessage({
                id: 'WorkflowRoutingWelcome.initialSuggestionsUnavailable',
                defaultMessage:
                  'Initial suggestions are not available right now, please try another time or use the input field for a specific request.',
                description:
                  'Message shown when initial suggestions could not be loaded',
              })}
              variant="body"
              iconSpacing="m"
            />
          </Layout>
        )}

        {!isInitialLoad && hasSuggestions && (
          <Layout
            direction="column"
            spacing={4}
            w="100%"
            role="list"
            aria-label={intl.formatMessage({
              id: 'WorkflowRoutingWelcome.suggestedPrompts',
              defaultMessage: 'Suggested prompts',
              description: 'Accessibility label for suggested prompts list',
            })}
          >
            {suggestions.map((suggestion, index) => (
              <Layout
                key={`suggestion-${index}`}
                role="listitem"
                cursor="pointer"
                styles={{
                  '&:hover > div': {
                    backgroundColor: 'status.info.secondary',
                  },
                }}
              >
                <Card
                  role="button"
                  mode="bordered"
                  size="s"
                  borderRadius="l"
                  width="100%"
                  onClick={() => handleSuggestionClick(suggestion)}
                  tabIndex={0}
                  onKeyDown={(e: KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSuggestionClick(suggestion);
                    }
                  }}
                >
                  <Layout py={0} w="100%" borderRadius="m">
                    <StatusText
                      icon="sparkle-single-small"
                      iconColor="status.info"
                      text={suggestion}
                      variant="body"
                      iconSpacing="m"
                    />
                  </Layout>
                </Card>
              </Layout>
            ))}
          </Layout>
        )}
      </Layout>
    </Layout>
  );
};
